import { createSlice } from "@reduxjs/toolkit";
import LocalStorageService from "../../common/services/LocalStorageService";
import { logOut } from "../auth/authSlice";
import {
  getBasket,
  setBasketItem,
  removeBasketItem,
  mergeBasket,
} from "./cardActions";

const initialState = {
  loading: false,
  error: null,
  items: LocalStorageService.get("items") || [],
  success: false,
};

const cardSlice = createSlice({
  name: "card",
  initialState,
  reducers: {
    addCardItem: (state, action) => {
      const newItems = [...state.items, action?.payload].reduce((acc, item) => {
        // Check if the item already exists in the accumulator
        const foundIndex = acc.findIndex(
          (el) =>
            el?.id?.toString() === item?.id?.toString() &&
            el?.variant?.id?.toString() === item?.variant?.id?.toString()
        );

        if (foundIndex !== -1) {
          // If the item exists, update the quantity
          acc[foundIndex].quantity += item?.quantity;
        } else {
          // If the item does not exist, add it to the accumulator
          acc?.push(item);
        }

        return acc;
      }, []);

      LocalStorageService.set("items", newItems);
      state.items = newItems;
    },
    setCard: (state, action) => {
      const newItems = action.payload;
      LocalStorageService.set("items", newItems);
      state.items = newItems;
    },
    // Empties the cart after an order without leaving a guest-storage
    // key behind (unlike setCard([]), which would write "items": []).
    clearCard: (state) => {
      LocalStorageService.remove("items");
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    // Server-basket flows (authenticated patients): the server response is the
    // source of truth, localStorage "items" is NOT written — it stays a
    // guest-only store.
    const applyServerItems = (state, action) => {
      state.loading = false;
      state.error = null;
      state.items = action.payload;
    };
    const applyPending = (state) => {
      state.loading = true;
    };
    const applyRejected = (state, action) => {
      state.loading = false;
      state.error = action.payload;
    };
    builder
      .addCase(getBasket.pending, applyPending)
      .addCase(getBasket.fulfilled, applyServerItems)
      .addCase(getBasket.rejected, applyRejected)
      .addCase(setBasketItem.pending, applyPending)
      .addCase(setBasketItem.fulfilled, applyServerItems)
      .addCase(setBasketItem.rejected, applyRejected)
      .addCase(removeBasketItem.pending, applyPending)
      .addCase(removeBasketItem.fulfilled, applyServerItems)
      .addCase(removeBasketItem.rejected, applyRejected)
      .addCase(mergeBasket.pending, applyPending)
      .addCase(mergeBasket.fulfilled, applyServerItems)
      .addCase(mergeBasket.rejected, applyRejected)
      // Logout: the basket stays saved on the server for next login; the
      // local copy is cleared so the next guest on this browser starts empty.
      .addCase(logOut, (state) => {
        state.items = [];
        LocalStorageService.remove("items");
      });
  },
});
export const { addCardItem, setCard, clearCard } = cardSlice.actions;
export default cardSlice.reducer;
